/**
 * Single source for API base URL.
 * - Empty string → same-origin relative `/api/*` (no CORS).
 * - Production: set REACT_APP_API_URL when the SPA and API are on different hosts (e.g. Vercel + Render).
 *
 * Important: If REACT_APP_API_URL points to production (e.g. onrender.com) but you open the
 * SPA from `localhost`, browsers will block those calls (CORS). For local dev (`npm run server`
 * or CRA on localhost), we always use same-origin `/api` unless REACT_APP_USE_REMOTE_API=true.
 */
export const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    const forceRemote =
      process.env.REACT_APP_USE_REMOTE_API === 'true' ||
      process.env.REACT_APP_USE_REMOTE_API === '1';
    if ((h === 'localhost' || h === '127.0.0.1') && !forceRemote) {
      return '';
    }
  }

  const env = process.env.REACT_APP_API_URL;
  if (env === undefined || env === '') return '';
  const trimmed = env.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    try {
      if (new URL(trimmed).origin === window.location.origin) {
        return '';
      }
    } catch {
      return trimmed;
    }
  }
  return trimmed;
};

/**
 * Absolute URL for API paths (prepends REACT_APP_API_URL when set; otherwise same-origin relative path).
 */
export const apiUrl = (path: string): string => {
  const base = getApiBaseUrl();
  const p = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${p}` : p;
};
